import { useEffect, useRef, useState } from 'react';
import {
  createEmbeddingContext,
  DashboardExperience,
  EmbeddingEvents,
  EventListener,
  MessageEventName,
} from 'amazon-quicksight-embedding-sdk';
import { connect } from 'react-redux';
import { getQuickSightLocale } from '@Utils';

import { sendCustomEvent, sendErrorEvent } from '@Analytics';
import { ErrorMessage } from '@Components';
import { AppDispatch, MiscellaneousActions, RootState } from '@Store';
import { Container, DashboardContainer, PoweredByBanner } from './monitor.styles';

interface MonitorProps {
  dashboardUrl: string;
  sheet?: string;
  acknowledgedDashboardRefresh: () => void;
  dashboardRefresh: boolean;
  language: string;
  hideBookmark?: boolean;
  retailer?: string | null;
  dashboardName?: string;
}

const Monitor = ({
  dashboardUrl,
  sheet,
  acknowledgedDashboardRefresh,
  dashboardRefresh,
  language,
  hideBookmark,
  retailer,
  dashboardName,
}: MonitorProps) => {
  const isSmbsDashboard = dashboardName?.toLowerCase().includes('smb') ?? false;
  const [dashboardError, setDashboardError] = useState<string>('');
  const embeddedDashboardExperienceRef = useRef<DashboardExperience | null>(null);
  const containerDiv = useRef<HTMLInputElement>(null);
  const hasRun = useRef(false);

  const cleanDashboardContainerDiv = () => {
    if (containerDiv?.current?.children && containerDiv?.current?.children.length > 0)
      while (containerDiv?.current?.children?.length > 0)
        if (containerDiv?.current?.children[0]) containerDiv?.current?.removeChild(containerDiv.current.children[0]);
  };

  useEffect(() => {
    const buildDashboard = async () => {
      const onMessageHandler: EventListener = (event: EmbeddingEvents) => {
        switch (event.eventName) {
          case MessageEventName.ERROR_OCCURRED: {
            sendErrorEvent(`Error occurred while rendering the experience. Error: ${event.message?.errorCode}`);
            break;
          }
          case MessageEventName.PARAMETERS_CHANGED: {
            sendCustomEvent('QUICKSIGHT_PARAMETERS_CHANGED', { retailer });
            break;
          }
          case MessageEventName.SELECTED_SHEET_CHANGED: {
            sendCustomEvent('QUICKSIGHT_SELECTED_SHEET_CHANGED', {
              sheetId: event.message?.selectedSheet?.SheetId,
              retailer,
            });
            break;
          }
          case MessageEventName.RESET: {
            sendCustomEvent('QUICKSIGHT_RESET', { retailer });
            break;
          }
          case MessageEventName.MODAL_OPENED: {
            embeddedDashboardExperienceRef.current
              ?.getSelectedSheetId()
              .then((selectedSheetId) => {
                sendCustomEvent('QUICKSIGHT_MODAL_OPENED', {
                  modalName: (event.message as any).modalName,
                  selectedSheetId,
                  contextId: event.eventTarget?.contextId,
                  retailer,
                });
              })
              .catch((e) => {
                sendCustomEvent('QUICKSIGHT_MODAL_OPENED', {
                  modalName: (event.message as any).modalName,
                  selectedSheetId: `Unable to get, error occurred ${e}`,
                  retailer,
                });
              });
            break;
          }
          case 'CONTENT_LOADED':
          case 'SIZE_CHANGED':
          case 'GET_SELECTED_SHEET_ID':
            // No need to send this events to RUM
            break;

          default:
            break;
        }
      };

      const embeddingContext = await createEmbeddingContext();

      cleanDashboardContainerDiv();

      const embeddedDashboard = await embeddingContext.embedDashboard(
        {
          url: dashboardUrl,
          container: '#my-qs-frame',
          withIframePlaceholder: true,
          // onChange: (changeEvent, metadata) => { },
        },
        {
          sheetOptions: { initialSheetId: sheet, fitSheetToWidth: !isSmbsDashboard },
          onMessage: onMessageHandler,
          locale: language,
          toolbarOptions: {
            export: true,
            undoRedo: true,
            reset: true,
            bookmarks: !hideBookmark,
          },
        },
      );

      embeddedDashboardExperienceRef.current = embeddedDashboard;
    };

    if (!hasRun.current) {
      hasRun.current = true;

      buildDashboard().catch((e) => {
        return setDashboardError(e);
      });
    }

    return () => {
      // HACK: This is a workaround to avoid the error "Creating the frame timed out"
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (embeddedDashboardExperienceRef.current?.experienceFrame.timeoutInstance) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        clearTimeout(embeddedDashboardExperienceRef.current?.experienceFrame.timeoutInstance);
      }
    };
  }, []);

  if (dashboardError) return <ErrorMessage internalMessage={`Error loading dashboard: ${dashboardError}`} />;

  useEffect(() => {
    if (!dashboardRefresh) return;

    embeddedDashboardExperienceRef.current?.reset();
    acknowledgedDashboardRefresh();
  }, [dashboardRefresh]);

  return (
    <Container $padded={isSmbsDashboard}>
      <DashboardContainer id="my-qs-frame" ref={containerDiv} />
      <PoweredByBanner>© {new Date().getFullYear()} bops. All rights reserved.</PoweredByBanner>
    </Container>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  const { updateValue: dispatchUpdateValue } = MiscellaneousActions;

  return {
    acknowledgedDashboardRefresh: () => dispatch(dispatchUpdateValue({ dashboardRefresh: false })),
  };
};

const mapStateToProps = (state: RootState) => {
  const { miscellaneous, client } = state;
  const { dashboardRefresh } = miscellaneous;
  const { configuration } = client || {};
  const language = getQuickSightLocale(configuration?.language);

  return { dashboardRefresh, language };
};

export default connect(mapStateToProps, mapDispatchToProps)(Monitor);
